import React from 'react';
import { DocsLayout } from '../components/DocsLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText } from 'lucide-react';

export const DataProcessingAgreementPage: React.FC = () => {
  return (
    <DocsLayout
      title='Data Processing Agreement'
      description="How we handle your organization's data"
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Legal', path: '/docs/legal' },
        { label: 'Data Processing' }
      ]}
    >
      <div className='max-w-[1000px]'>
        <p className='text-sm text-muted-foreground italic mb-8'>Last Updated: January 28, 2026</p>

        <div className='mb-8'>
          <Card className='border-blue-500/50 bg-blue-500/5'>
            <CardHeader>
              <div className='flex items-center gap-2'>
                <FileText className='h-5 w-5 text-blue-600' />
                <CardTitle className='text-lg text-blue-700 dark:text-blue-400'>
                  What This Document Covers
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className='text-sm leading-6'>
              <p>
                This agreement explains how Kebi AI handles data on behalf of your organization when
                you use Vehix. It is written in plain language so you understand exactly what
                happens to your data.
              </p>
            </CardContent>
          </Card>
        </div>

        <section className='mb-20' id='our-roles'>
          <h2 className='text-3xl font-bold mb-6'>1. Who Does What</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>1.1 Your Organization (Data Controller)</h3>
            <p className='text-sm leading-6 mb-4'>Your organization decides:</p>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>What data to put into Vehix</li>
              <li className='text-sm leading-6'>Who in your organization can access the data</li>
              <li className='text-sm leading-6'>How long to keep the data</li>
              <li className='text-sm leading-6'>When to delete the data</li>
            </ul>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>1.2 Kebi AI (Data Processor)</h3>
            <p className='text-sm leading-6 mb-4'>We handle your data only to:</p>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>Provide the Vehix service to you</li>
              <li className='text-sm leading-6'>Keep your data secure</li>
              <li className='text-sm leading-6'>Follow your instructions about your data</li>
              <li className='text-sm leading-6'>Help you meet your legal obligations</li>
            </ul>
          </div>
        </section>

        <section className='mb-20' id='what-we-do'>
          <h2 className='text-3xl font-bold mb-6'>2. What We Do With Your Data</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>2.1 We Only Use Data to Provide Service</h3>
            <p className='text-sm leading-6'>
              We process your data only to make Vehix work for you. We do not use your data for
              anything else without your permission.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>2.2 We Follow Your Instructions</h3>
            <p className='text-sm leading-6'>
              If you tell us to do something with your data (like delete it), we follow your
              instructions as long as it is legal and reasonable.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>2.3 We Keep It Confidential</h3>
            <p className='text-sm leading-6'>
              Everyone at Kebi AI who might see your data has agreed to keep it confidential. We do
              not share your data with people who do not need to see it.
            </p>
          </div>
        </section>

        <section className='mb-20' id='keeping-data-safe'>
          <h2 className='text-3xl font-bold mb-6'>3. How We Keep Your Data Safe</h2>
          <p className='text-sm leading-6 mb-4'>We protect your data with:</p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>
              <strong>Encryption:</strong> Your data is scrambled so only authorized systems can
              read it
            </li>
            <li className='text-sm leading-6'>
              <strong>Access controls:</strong> Only people who need access can see your data
            </li>
            <li className='text-sm leading-6'>
              <strong>Regular security checks:</strong> We test our systems for weaknesses
            </li>
            <li className='text-sm leading-6'>
              <strong>Staff training:</strong> Our team knows how to handle data safely
            </li>
            <li className='text-sm leading-6'>
              <strong>Monitoring:</strong> We watch for unusual activity that might indicate a
              problem
            </li>
          </ul>
        </section>

        <section className='mb-20' id='other-companies'>
          <h2 className='text-3xl font-bold mb-6'>4. Other Companies That Help Us</h2>
          <p className='text-sm leading-6 mb-4'>
            We use other trusted companies to help run Vehix. These companies:
          </p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>Only see data they need to provide their service</li>
            <li className='text-sm leading-6'>Have agreed to protect your data</li>
            <li className='text-sm leading-6'>Meet our security standards</li>
          </ul>
          <p className='text-sm leading-6 mt-4'>
            See our{' '}
            <a href='/docs/legal/service-providers' className='text-primary hover:underline'>
              Service Providers page
            </a>{' '}
            for a list of these companies.
          </p>
        </section>

        <section className='mb-20' id='your-requests'>
          <h2 className='text-3xl font-bold mb-6'>5. Helping With Data Requests</h2>
          <p className='text-sm leading-6 mb-4'>
            If someone asks you for their personal data (like an employee or customer), we will help
            you respond. We can:
          </p>
          <ul className='list-disc pl-6 space-y-2'>
            <li className='text-sm leading-6'>Help you find their data in Vehix</li>
            <li className='text-sm leading-6'>Export their data in a common format</li>
            <li className='text-sm leading-6'>Delete their data if you ask us to</li>
            <li className='text-sm leading-6'>Correct any mistakes in their data</li>
          </ul>
        </section>

        <section className='mb-20' id='security-issues'>
          <h2 className='text-3xl font-bold mb-6'>6. If Something Goes Wrong</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>6.1 Security Problems</h3>
            <p className='text-sm leading-6'>
              If we discover a security problem that might affect your data, we will tell you as
              soon as possible (within 72 hours). We will explain what happened and what we are
              doing about it.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>6.2 What We Will Tell You</h3>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>What happened</li>
              <li className='text-sm leading-6'>What data might be affected</li>
              <li className='text-sm leading-6'>What we are doing to fix it</li>
              <li className='text-sm leading-6'>What you might need to do</li>
            </ul>
          </div>
        </section>

        <section className='mb-20' id='when-you-leave'>
          <h2 className='text-3xl font-bold mb-6'>7. When You Stop Using Vehix</h2>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>7.1 Getting Your Data</h3>
            <p className='text-sm leading-6'>
              When you end your subscription, you can request a copy of all your data. We will
              provide it in a common format (like CSV or JSON) within 30 days.
            </p>
          </div>

          <div className='mb-8'>
            <h3 className='text-lg font-semibold mb-4'>7.2 Deleting Your Data</h3>
            <p className='text-sm leading-6 mb-4'>
              After you leave, we will delete your data from our systems within 90 days. Some data
              may be kept longer if:
            </p>
            <ul className='list-disc pl-6 space-y-2'>
              <li className='text-sm leading-6'>The law requires us to keep it</li>
              <li className='text-sm leading-6'>
                It is in our backup systems (deleted in normal backup rotation)
              </li>
            </ul>
          </div>
        </section>

        <section className='mb-20' id='where-data-stored'>
          <h2 className='text-3xl font-bold mb-6'>8. Where Your Data Is Stored</h2>
          <p className='text-sm leading-6'>
            Your data is stored on secure servers in the United States. If you are located outside
            the US, by using Vehix you agree that your data may be transferred to and stored in the
            US.
          </p>
        </section>

        <section className='mb-20' id='audits'>
          <h2 className='text-3xl font-bold mb-6'>9. Checking Our Work</h2>
          <p className='text-sm leading-6'>
            You have the right to verify that we are handling your data properly. If you have
            concerns, contact us and we will work with you to address them.
          </p>
        </section>

        <section className='mb-20' id='contact'>
          <h2 className='text-3xl font-bold mb-6'>10. Contact Us</h2>
          <p className='text-sm leading-6 mb-6'>
            For questions about data processing or to make a request:
          </p>

          <div className='p-6 bg-muted/30 rounded-lg border border-border'>
            <p className='text-sm leading-6'>
              <strong>Kebi AI LLC</strong>
              <br />
              Data Protection: <span className='text-primary'>privacy@kebi.ai</span>
              <br />
              Support: <span className='text-primary'>support@kebi.ai</span>
            </p>
          </div>
        </section>
      </div>
    </DocsLayout>
  );
};
